'use client';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  PropertyAddressSchema,
  addressLabel,
  type PropertyAddressInput,
} from '~/lib/addresses/schema';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '~/components/ui/combobox';
export type SelectedAddress = {
  address: string;
  propertyAddress?: PropertyAddressInput;
  parcel?: { block: string; lot: string };
};
const querySchema = z.object({ query: z.string().max(200) });
const suggestionsSchema = z.object({
  suggestions: z.array(z.object({ id: z.string(), label: z.string() })),
});
export function SignedInPropertyLookup({
  onSelect,
  initialStreet = '',
}: {
  onSelect: (value: SelectedAddress) => void;
  initialStreet?: string;
}) {
  const [state, setState] = useState({
    manual: false,
    debounced: '',
    pending: false,
    error: '',
    session: crypto.randomUUID(),
    selected: null as PropertyAddressInput | null,
  });
  const search = useForm({
    resolver: zodResolver(querySchema),
    defaultValues: { query: initialStreet },
  });
  const query = useWatch({ control: search.control, name: 'query' });
  // Debounced external provider request; abort obsolete reads on query changes.
  useEffect(() => {
    const timer = setTimeout(() => setState((s) => ({ ...s, debounced: query ?? '' })), 220);
    return () => clearTimeout(timer);
  }, [query]);
  const suggestions = useQuery({
    queryKey: ['google-address', state.session, state.debounced],
    enabled: !state.manual && state.debounced.length >= 3,
    queryFn: async ({ signal }) => {
      const r = await fetch(
        `/api/addresses?q=${encodeURIComponent(state.debounced)}&session=${state.session}`,
        { signal },
      );
      if (!r.ok) throw new Error('Search unavailable');
      return suggestionsSchema.parse(await r.json()).suggestions;
    },
    retry: false,
  });
  const manual = useForm({
    resolver: zodResolver(PropertyAddressSchema),
    defaultValues: {
      street: initialStreet,
      unit: '',
      city: '',
      region: '',
      postalCode: '',
      country: 'US' as const,
    },
  });
  async function select(id: string) {
    setState((s) => ({ ...s, pending: true, error: '' }));
    try {
      const r = await fetch(
        `/api/addresses?place=${encodeURIComponent(id)}&session=${state.session}`,
      );
      if (!r.ok) throw new Error();
      const address = PropertyAddressSchema.parse(await r.json());
      manual.reset(address);
      setState((s) => ({ ...s, manual: true, pending: false, selected: address }));
    } catch {
      setState((s) => ({
        ...s,
        pending: false,
        error: 'Choose a street address or enter the property address below.',
      }));
    }
  }
  if (state.manual)
    return (
      <Form {...manual}>
        <form
          className="space-y-4 pt-4"
          data-test="manual-property-address"
          onSubmit={manual.handleSubmit((value) => {
            const same =
              state.selected &&
              (['street', 'city', 'region', 'postalCode'] as const).every(
                (k) => value[k] === state.selected![k],
              );
            const propertyAddress = same
              ? value
              : { ...value, placeId: undefined, latitude: undefined, longitude: undefined };
            onSelect({ propertyAddress, address: addressLabel(propertyAddress) });
          })}
        >
          <p className="text-sm text-muted-foreground">
            Confirm the property address. A parcel number is not required.
          </p>
          {(['street', 'unit', 'city', 'region', 'postalCode'] as const).map((name) => (
            <FormField
              key={name}
              control={manual.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {
                      {
                        street: 'Street address',
                        unit: 'Unit / apartment (optional)',
                        city: 'City',
                        region: 'State',
                        postalCode: 'ZIP code',
                      }[name]
                    }
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete={
                        {
                          street: 'street-address',
                          unit: 'address-line2',
                          city: 'address-level2',
                          region: 'address-level1',
                          postalCode: 'postal-code',
                        }[name]
                      }
                      data-test={`address-${name}`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setState((s) => ({ ...s, manual: false }))}
            >
              Back
            </Button>
            <Button type="submit" data-test="confirm-property-address">
              Continue
            </Button>
          </div>
        </form>
      </Form>
    );
  return (
    <Form {...search}>
      <form className="space-y-4 pt-4" onSubmit={(e) => e.preventDefault()}>
        <FormField
          control={search.control}
          name="query"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Property address</FormLabel>
              <Combobox<{ id: string; label: string }>
                filter={null}
                items={suggestions.data ?? []}
                itemToStringLabel={(item) => item.label}
                onValueChange={(value) => {
                  if (value) void select(value.id);
                }}
              >
                <FormControl>
                  <ComboboxInput
                    placeholder="Search a US street address"
                    value={field.value}
                    onChange={field.onChange}
                    autoFocus
                    data-test="property-address-search"
                  />
                </FormControl>
                <ComboboxContent>
                  <ComboboxEmpty>
                    {suggestions.isFetching ? 'Searching…' : 'No matching address'}
                  </ComboboxEmpty>
                  <ComboboxList>
                    {(item) => (
                      <ComboboxItem key={item.id} value={item}>
                        {item.label}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
              <FormMessage />
            </FormItem>
          )}
        />
        {state.pending && <Spinner />}
        {(state.error || suggestions.isError) && (
          <p role="alert" className="text-sm">
            {state.error || 'Address search is unavailable. You can enter the address manually.'}
          </p>
        )}
        <p className="text-xs text-muted-foreground">Address suggestions by Google Maps</p>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            manual.setValue('street', query ?? '');
            setState((s) => ({ ...s, manual: true }));
          }}
          data-test="manual-address-button"
        >
          Enter address manually
        </Button>
      </form>
    </Form>
  );
}
